import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import ComingSoon from '../pages/ComingSoon';

const isDev = import.meta.env.MODE === 'development';

const PasswordGate = ({ children }) => {
    // Dynamic Canonical Tag Logic
    // We use window.location because during client-side nav, we want the current browser URL
    // This runs for both authenticated and unauthenticated users
    const canonicalUrl = `https://unboxcreator.com${window.location.pathname === '/' ? '' : window.location.pathname}`;

    // Synchronous initialization to ensure Bots see content on First Paint

    const [isAuthenticated, setIsAuthenticated] = useState(() => {
        if (typeof window === 'undefined') return false; // Safety for build time

        // 1. Explicit Path Bypass (Flux de Vérité)
        const isOpenPath = window.location.pathname.startsWith('/ai-knowledge');
        if (isOpenPath) return true;

        // 2. Session Check
        const authenticated = sessionStorage.getItem('unbox_authenticated');
        if (authenticated === 'true') return true;

        return false;
    });

    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [showPasswordPrompt, setShowPasswordPrompt] = useState(false);

    // Mot de passe pour accès preview (vous pouvez le changer!)
    const PREVIEW_PASSWORD = import.meta.env.VITE_PREVIEW_PASSWORD;


    const handlePasswordSubmit = (e) => {
        e.preventDefault();

        if (PREVIEW_PASSWORD && password === PREVIEW_PASSWORD) {
            sessionStorage.setItem('unbox_authenticated', 'true');
            setIsAuthenticated(true);
            setError('');
        } else {
            setError('Acces refuse');
            setPassword('');
        }
    };

    const handleLogout = () => {
        sessionStorage.removeItem('unbox_authenticated');
        setIsAuthenticated(false);
        setShowPasswordPrompt(false);
    };

    // Si pas authentifié, montrer Coming Soon
    if (!isAuthenticated) {
        return (
            <>
                <Helmet>
                    <link rel="canonical" href={canonicalUrl} />
                </Helmet>
                <ComingSoon onAccessClick={() => setShowPasswordPrompt(true)} />

                {/* Modal mot de passe */}
                {showPasswordPrompt && (
                    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/90 backdrop-blur-sm">
                        <div className="relative bg-gradient-to-br from-gray-900 to-black border-2 border-[var(--primary)] rounded-3xl p-10 max-w-md w-full mx-4 shadow-2xl">
                            <button
                                onClick={() => setShowPasswordPrompt(false)}
                                className="absolute top-4 right-4 text-white/50 hover:text-white transition-colors"
                            >
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>

                            <div className="text-center mb-8">
                                <div className="inline-flex items-center justify-center w-16 h-16 bg-[var(--primary)]/20 rounded-full mb-4">
                                    <svg className="w-8 h-8 text-[var(--primary)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                    </svg>
                                </div>
                                <h3 className="text-2xl font-bold text-white mb-2">Accès Preview</h3>
                                <p className="text-white/60 text-sm">Entrez le mot de passe pour accéder au site</p>
                            </div>

                            <form onSubmit={handlePasswordSubmit} className="space-y-4">
                                <div>
                                    <input
                                        type="password"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        placeholder="Mot de passe"
                                        className="w-full px-6 py-4 bg-white/5 border-2 border-white/10 rounded-xl text-white placeholder-white/40 focus:outline-none focus:border-[var(--primary)] transition-all"
                                        autoFocus
                                    />
                                    {error && (
                                        <p className="mt-2 text-red-400 text-sm flex items-center gap-2">
                                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                            </svg>
                                            {error}
                                        </p>
                                    )}
                                </div>

                                <button
                                    type="submit"
                                    className="w-full px-6 py-4 bg-gradient-to-r from-[var(--primary)] to-[var(--primary-dim)] text-black font-bold rounded-xl hover:scale-105 transition-transform duration-300 shadow-lg hover:shadow-[0_0_30px_var(--primary)]"
                                >
                                    Accéder au site
                                </button>
                            </form>

                            <p className="mt-6 text-center text-xs text-white/40">
                                Réservé aux membres de l'équipe
                            </p>
                        </div>
                    </div>
                )}
            </>
        );
    }

    // Si mode dev, on bypass tout MAIS on a quand même exécuté les hooks pour respecter React
    if (isDev) {
        return <>{children}</>;
    }

    // Si authentifié, montrer le site normal AVEC un bouton logout discret
    return (
        <>
            <Helmet>
                <link rel="canonical" href={canonicalUrl} />
            </Helmet>
            {children}

            {/* Logout button removed as per user request */}
        </>
    );
};

export default PasswordGate;

